/**
 * User configuration for the Impact Agent
 * Stored in OneDrive: /Apps/progress-agent/config/user-config.json
 */
export interface UserConfig {
  version: '1.0';
  userId: string;

  /** Proactive scheduling configuration */
  schedule: ScheduleConfig;

  /** Search and tracking parameters */
  tracking: TrackingConfig;

  /** Content sources configuration */
  sources: SourcesConfig;

  /** Output format preferences */
  output: OutputConfig;

  /** UI preferences for companion app */
  ui: UIConfig;

  /** Notification preferences for agent updates */
  notifications: NotificationConfig;

  /** Data source feature toggles (chats, files, email, calendar) */
  dataSources: DataSourceConfig;

  /** Consent state for tracking permission grants */
  consentState?: ConsentStateConfig;

  updatedUtc: string;
}

/**
 * Data source feature toggles
 * Controls which sources the agent searches for evidence
 */
export interface DataSourceConfig {
  /** Teams chats - enabled by default (part of base scopes) */
  chats: boolean;
  /** OneDrive/SharePoint files - enabled by default (part of base scopes) */
  files: boolean;
  /** Email - disabled by default, requires incremental consent */
  email: boolean;
  /** Calendar - disabled by default, requires incremental consent */
  calendar: boolean;
}

/**
 * Consent state configuration
 * Tracks which permissions have been granted
 */
export interface ConsentStateConfig {
  /** Whether base scopes have been granted */
  baseConsented: boolean;
  /** Timestamp of base consent */
  baseConsentedAt?: string;
  /** Whether email scope has been granted */
  emailConsented: boolean;
  /** Timestamp of email consent */
  emailConsentedAt?: string;
  /** Whether calendar scope has been granted */
  calendarConsented: boolean;
  /** Timestamp of calendar consent */
  calendarConsentedAt?: string;
  /** Whether using Chat.Read fallback instead of ChatMessage.Read */
  chatReadFallback: boolean;
  /** Whether email requires admin consent in this tenant */
  emailRequiresAdmin: boolean;
  /** Whether calendar requires admin consent in this tenant */
  calendarRequiresAdmin: boolean;
  /** Last time consent was checked */
  lastCheckedAt?: string;
}

export interface ScheduleConfig {
  /** Whether proactive mode is enabled */
  enabled: boolean;
  /** Preferred day for weekly check-in */
  preferredDay: 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday';
  /** Preferred time in HH:mm format */
  preferredTime: string;
  /** IANA timezone identifier */
  timezone: string;
  /** How often the agent should check in */
  cadence: 'weekly' | 'biweekly' | 'monthly';
}

export interface TrackingConfig {
  /** Keywords to search for (e.g., project names, OKR tags) */
  keywords: string[];
  /** Named project groupings */
  projects: string[];
  /** Colleague AAD IDs or emails to include in searches */
  colleagues: string[];
  /** Teams channel IDs to monitor */
  channels: string[];
  /** Regex patterns for content to exclude */
  excludePatterns: string[];
}

/**
 * Content sources configuration
 * Defines where the agent should look for relevant work
 */
export interface SourcesConfig {
  /** Key people to prioritize in searches */
  people: PersonSource[];
  /** Conversations (chats, group chats, meetings) to monitor */
  conversations: ConversationSource[];
  /** Document libraries and folders to search */
  documents: DocumentSource[];
}

/**
 * A person to prioritize in searches
 */
export interface PersonSource {
  id: string;
  /** Display name */
  name: string;
  /** Email address */
  email: string;
  /** AAD user ID (if available) */
  userId?: string;
  /** Profile photo URL */
  photoUrl?: string;
  /** Priority weight (1-5, higher = more important) */
  weight: number;
  /** Relationship context */
  relationship?: 'manager' | 'direct-report' | 'peer' | 'stakeholder' | 'external';
}

/**
 * A conversation source (chat, group chat, channel, or meeting series)
 */
export interface ConversationSource {
  id: string;
  /** Display name */
  name: string;
  /** Type of conversation */
  type: 'chat' | 'group-chat' | 'channel' | 'meeting-series';
  /** Teams/Outlook ID */
  sourceId: string;
  /** Parent team name (for channels) */
  teamName?: string;
  /** Priority weight (1-5) */
  weight: number;
  /** Whether this source is currently active */
  enabled: boolean;
}

/**
 * A document source (SharePoint library, OneDrive folder, etc.)
 */
export interface DocumentSource {
  id: string;
  /** Display name */
  name: string;
  /** Type of document source */
  type: 'sharepoint-library' | 'sharepoint-folder' | 'onedrive-folder' | 'teams-files';
  /** SharePoint/Graph URL or path */
  path: string;
  /** Site name (for SharePoint) */
  siteName?: string;
  /** Priority weight (1-5) */
  weight: number;
  /** Whether this source is currently active */
  enabled: boolean;
}

export interface OutputConfig {
  /** Output file format */
  format: 'markdown' | 'docx';
  /** Include source links in output */
  includeLinks: boolean;
  /** Include timestamps on entries */
  includeTimestamps: boolean;
  /** Maximum number of highlights to include */
  maxHighlights: number;
}

export interface UIConfig {
  /** Color theme preference */
  theme: 'light' | 'dark' | 'system';
  /** Use compact layout */
  compactMode: boolean;
}

/**
 * Notification preferences for proactive agent updates
 */
export interface NotificationConfig {
  /** Receive notifications via email */
  email: boolean;
  /** Receive notifications via Teams */
  teams: boolean;
}

/**
 * Default data source configuration
 * Chats and Files are ON by default (base scopes)
 * Email and Calendar are OFF by default (extended scopes)
 */
export const DEFAULT_DATA_SOURCES: DataSourceConfig = {
  chats: true,
  files: true,
  email: false,
  calendar: false,
};

/**
 * Default consent state
 */
export const DEFAULT_CONSENT_STATE: ConsentStateConfig = {
  baseConsented: false,
  emailConsented: false,
  calendarConsented: false,
  chatReadFallback: false,
  emailRequiresAdmin: false,
  calendarRequiresAdmin: false,
};

/**
 * Create a default user configuration
 */
export function createDefaultConfig(userId: string): UserConfig {
  return {
    version: '1.0',
    userId,
    schedule: {
      enabled: false,
      preferredDay: 'friday',
      preferredTime: '09:00',
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      cadence: 'weekly',
    },
    tracking: {
      keywords: [],
      projects: [],
      colleagues: [],
      channels: [],
      excludePatterns: [],
    },
    sources: {
      people: [],
      conversations: [],
      documents: [],
    },
    output: {
      format: 'markdown',
      includeLinks: true,
      includeTimestamps: true,
      maxHighlights: 10,
    },
    ui: {
      theme: 'system',
      compactMode: false,
    },
    notifications: {
      email: true,
      teams: true,
    },
    dataSources: DEFAULT_DATA_SOURCES,
    consentState: DEFAULT_CONSENT_STATE,
    updatedUtc: new Date().toISOString(),
  };
}



